import { updateQuantityOrderThunk } from '@redux/features/order.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    orderId: number;
    itemId: number;
    currentQuantity: number;
    onUpdateComplete?: () => void; // Callback to refresh order data after updating
};

const UpdateQuantityModal: FC<IProps> = ({ show, handleClose, orderId, itemId, currentQuantity, onUpdateComplete }) => {
    const dispatch = useAppDispatch();
    const { updateQuantityOrderLoading } = useAppSelector((state: RootState) => state.orderState);

    const validationSchema = Yup.object({
        quantity: Yup.number()
            .required('Quantity is required')
            .min(1, 'Quantity must be at least 1')
            .integer('Quantity must be an integer'),
    });

    const formik = useFormik({
        initialValues: {
            quantity: currentQuantity,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            dispatch(
                updateQuantityOrderThunk({
                    orderId: orderId,
                    body: [
                        {
                            itemId: itemId,
                            quantity: values.quantity,
                        },
                    ],
                })
            ).then((res: any) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    toast.success('Quantity updated successfully', { autoClose: 3000 });
                    formik.resetForm();
                    handleClose();
                    // Refresh order data after successful update
                    if (onUpdateComplete) {
                        onUpdateComplete();
                    }
                }
            });
        },
    });

    // Update form values when currentQuantity changes
    useEffect(() => {
        if (show && currentQuantity) {
            formik.setFieldValue('quantity', currentQuantity);
        }
    }, [show, currentQuantity]);

    if (!show) return null;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[90%] md:max-w-[500px]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Quantity</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                        <input
                            type="number"
                            name="quantity"
                            value={formik.values.quantity}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={updateQuantityOrderLoading}
                            min="1"
                            className={`w-full px-3 py-2 border rounded-md ${
                                formik.touched.quantity && formik.errors.quantity ? 'border-red-500' : 'border-gray-300'
                            } ${updateQuantityOrderLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            placeholder="Enter quantity"
                        />
                        {formik.touched.quantity && formik.errors.quantity && (
                            <p className="text-red-500 text-xs mt-1">{formik.errors.quantity}</p>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                        <ButtonBase
                            innerText="Cancel"
                            type="button"
                            onClick={handleClose}
                            disabled={updateQuantityOrderLoading}
                            className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <ButtonBase
                            innerText={updateQuantityOrderLoading ? 'Updating...' : 'Update Quantity'}
                            type="submit"
                            isLoading={updateQuantityOrderLoading}
                            disabled={updateQuantityOrderLoading}
                            className="bg-yellow-600 hover:bg-yellow-700 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default UpdateQuantityModal;
